// レイヤースタイル ↔ ag-psd LayerEffectsInfo (純関数)。
// Photoshop の輪郭 (contour)、ノイズ、テクスチャ、パターンオーバーレイは持たないので読み飛ばす。
import type { LayerEffectsInfo, LayerEffectShadow, LayerEffectsOuterGlow, LayerEffectInnerGlow, LayerEffectBevel, LayerEffectSatin, LayerEffectSolidFill, LayerEffectGradientOverlay, LayerEffectStroke, UnitsValue, BlendMode as PsdBlendMode } from 'ag-psd'
import type { LayerStyle, BlendMode, ShadowFx, GlowFx, BevelFx, SatinFx, OverlayFx, StrokeFx } from '@/types/document'
import { colorToHex, hexToColor } from './adjustments'
import { blendModeToPsd, blendModeFromPsd } from './blend'

const px = (value: number): UnitsValue => ({ units: 'Pixels', value })
// spread / choke は ag-psd が Pixels 単位でしか書けない (値は Photoshop と同じ 0..100 の百分率)
const pct = (value: number): UnitsValue => ({ units: 'Pixels', value })
const num = (u: UnitsValue | number | undefined, d: number): number => (u === undefined ? d : typeof u === 'number' ? u : u.value)
const bm = (m: BlendMode): PsdBlendMode => blendModeToPsd(m)
const bmFrom = (m: string | undefined, d: BlendMode): BlendMode => {
  const r = blendModeFromPsd(m, d)
  return r === 'pass-through' ? d : r
}

/** 何も書くものが無ければ undefined */
export function stylesToPsd(s: LayerStyle | undefined): LayerEffectsInfo | undefined {
  if (!s) return undefined
  const out: LayerEffectsInfo = { scale: 1 }
  let any = false
  const shadow = (f: ShadowFx): LayerEffectShadow => ({ present: true, showInDialog: true, enabled: f.enabled, size: px(f.size), angle: f.angle, distance: px(f.distance), color: hexToColor(f.hex), blendMode: bm(f.blendMode), opacity: f.opacity, useGlobalLight: false, antialiased: false, choke: pct(f.spread), layerConceals: true })
  if (s.dropShadow) { out.dropShadow = [shadow(s.dropShadow)]; any = true }
  if (s.innerShadow) { out.innerShadow = [shadow(s.innerShadow)]; any = true }
  const glow = (f: GlowFx) => ({ present: true, showInDialog: true, enabled: f.enabled, size: px(f.size), color: hexToColor(f.hex), blendMode: bm(f.blendMode), opacity: f.opacity, antialiased: false, noise: 0, range: 0.5, choke: pct(f.spread), jitter: 0 })
  if (s.outerGlow) { out.outerGlow = { ...glow(s.outerGlow), source: 'edge' } as LayerEffectsOuterGlow; any = true }
  if (s.innerGlow) { out.innerGlow = { ...glow(s.innerGlow), source: s.innerGlow.source ?? 'edge', technique: 'softer' } as LayerEffectInnerGlow; any = true }
  if (s.bevel) {
    const b = s.bevel
    const bevel: LayerEffectBevel = {
      present: true, showInDialog: true, enabled: b.enabled, size: px(b.size), angle: b.angle, strength: b.depth,
      highlightBlendMode: 'screen', shadowBlendMode: 'multiply', highlightColor: hexToColor(b.highlightHex), shadowColor: hexToColor(b.shadowHex),
      style: b.style === 'outer' ? 'outer bevel' : b.style === 'emboss' ? 'emboss' : 'inner bevel',
      highlightOpacity: b.highlightOpacity, shadowOpacity: b.shadowOpacity, soften: px(b.soften), useGlobalLight: false, altitude: b.altitude, technique: 'smooth', direction: 'up'
    }
    out.bevel = bevel; any = true
  }
  if (s.satin) {
    const f = s.satin
    const satin: LayerEffectSatin = { present: true, showInDialog: true, enabled: f.enabled, size: px(f.size), blendMode: bm(f.blendMode), color: hexToColor(f.hex), antialiased: true, opacity: f.opacity, distance: px(f.distance), invert: f.invert, angle: f.angle }
    out.satin = satin; any = true
  }
  if (s.colorOverlay) {
    const f = s.colorOverlay
    const fill: LayerEffectSolidFill = { present: true, showInDialog: true, enabled: f.enabled, blendMode: bm(f.blendMode), color: hexToColor(f.hex), opacity: f.opacity }
    out.solidFill = [fill]; any = true
  }
  if (s.gradientOverlay) {
    const f = s.gradientOverlay
    const stops = f.stops ?? [{ pos: 0, hex: '#000000' }, { pos: 1, hex: '#ffffff' }]
    const g: LayerEffectGradientOverlay = {
      present: true, showInDialog: true, enabled: f.enabled, blendMode: bm(f.blendMode), opacity: f.opacity, align: true, scale: 1, dither: false, reverse: false,
      type: f.style ?? 'linear', angle: f.angle ?? 90,
      gradient: { name: 'Custom', type: 'solid', smoothness: 4096, colorStops: stops.map(st => ({ color: hexToColor(st.hex), location: st.pos, midpoint: 0.5 })), opacityStops: [{ opacity: 1, location: 0, midpoint: 0.5 }, { opacity: 1, location: 1, midpoint: 0.5 }] }
    }
    out.gradientOverlay = [g]; any = true
  }
  if (s.stroke) {
    const f = s.stroke
    const st: LayerEffectStroke = { present: true, showInDialog: true, enabled: f.enabled, overprint: false, size: px(f.size), position: f.position, fillType: 'color', blendMode: bm(f.blendMode), opacity: f.opacity, color: hexToColor(f.hex) }
    out.stroke = [st]; any = true
  }
  return any ? out : undefined
}

export interface StyleImportResult {
  styles: LayerStyle | undefined
  /** 読み飛ばした要素 */
  skipped: string[]
}

export function stylesFromPsd(e: LayerEffectsInfo | undefined): StyleImportResult {
  const skipped: string[] = []
  if (!e) return { styles: undefined, skipped }
  const disabled = !!e.disabled
  const out: LayerStyle = {}
  let any = false
  const shadow = (f: LayerEffectShadow, d: BlendMode): ShadowFx => ({ enabled: !disabled && f.enabled !== false, blendMode: bmFrom(f.blendMode, d), hex: colorToHex(f.color), opacity: f.opacity ?? 0.75, angle: f.angle ?? 120, distance: num(f.distance, 5), spread: num(f.choke, 0), size: num(f.size, 5) })
  if (e.dropShadow?.[0]) { out.dropShadow = shadow(e.dropShadow[0], 'multiply'); any = true; if (e.dropShadow.length > 1) skipped.push('複数のドロップシャドウ') }
  if (e.innerShadow?.[0]) { out.innerShadow = shadow(e.innerShadow[0], 'multiply'); any = true }
  const glow = (f: LayerEffectsOuterGlow | LayerEffectInnerGlow): GlowFx => ({ enabled: !disabled && f.enabled !== false, blendMode: bmFrom(f.blendMode, 'screen'), hex: colorToHex(f.color, '#ffffbe'), opacity: f.opacity ?? 0.75, spread: num(f.choke, 0), size: num(f.size, 5) })
  if (e.outerGlow) { out.outerGlow = glow(e.outerGlow); any = true }
  if (e.innerGlow) { out.innerGlow = { ...glow(e.innerGlow), source: e.innerGlow.source === 'center' ? 'center' : 'edge' }; any = true }
  if (e.bevel) {
    const b = e.bevel
    const style: BevelFx['style'] = b.style === 'outer bevel' ? 'outer' : b.style === 'emboss' || b.style === 'pillow emboss' || b.style === 'stroke emboss' ? 'emboss' : 'inner'
    if (b.style === 'pillow emboss' || b.style === 'stroke emboss') skipped.push(`ベベル ${b.style} はエンボスとして読み込み`)
    out.bevel = { enabled: !disabled && b.enabled !== false, style, depth: b.strength ?? 100, size: num(b.size, 5), soften: num(b.soften, 0), angle: b.angle ?? 120, altitude: b.altitude ?? 30, highlightHex: colorToHex(b.highlightColor, '#ffffff'), highlightOpacity: b.highlightOpacity ?? 0.75, shadowHex: colorToHex(b.shadowColor, '#000000'), shadowOpacity: b.shadowOpacity ?? 0.75 }
    any = true
  }
  if (e.satin) {
    const f = e.satin
    const satin: SatinFx = { enabled: !disabled && f.enabled !== false, blendMode: bmFrom(f.blendMode, 'multiply'), hex: colorToHex(f.color), opacity: f.opacity ?? 0.5, angle: f.angle ?? 19, distance: num(f.distance, 11), size: num(f.size, 14), invert: !!f.invert }
    out.satin = satin; any = true
  }
  if (e.solidFill?.[0]) {
    const f = e.solidFill[0]
    const o: OverlayFx = { enabled: !disabled && f.enabled !== false, blendMode: bmFrom(f.blendMode, 'normal'), opacity: f.opacity ?? 1, hex: colorToHex(f.color, '#ff0000') }
    out.colorOverlay = o; any = true
  }
  if (e.gradientOverlay?.[0]) {
    const f = e.gradientOverlay[0]
    const g = f.gradient
    if (g && g.type === 'solid') {
      const stops = g.colorStops.map(s => ({ pos: Math.max(0, Math.min(1, s.location)), hex: colorToHex(s.color) })).sort((a, b) => a.pos - b.pos)
      const style: OverlayFx['style'] = f.type === 'radial' ? 'radial' : 'linear'
      if (f.type && f.type !== 'linear' && f.type !== 'radial') skipped.push(`グラデーションオーバーレイ ${f.type} は線形として読み込み`)
      out.gradientOverlay = { enabled: !disabled && f.enabled !== false, blendMode: bmFrom(f.blendMode, 'normal'), opacity: f.opacity ?? 1, hex: '#000000', stops: f.reverse ? stops.map(s => ({ pos: 1 - s.pos, hex: s.hex })).reverse() : stops, angle: f.angle ?? 90, style }
      any = true
    } else skipped.push('ノイズグラデーションのオーバーレイ')
  }
  if (e.stroke?.[0]) {
    const f = e.stroke[0]
    if (f.fillType && f.fillType !== 'color') skipped.push(`境界線の塗り ${f.fillType} は単色として読み込み`)
    const st: StrokeFx = { enabled: !disabled && f.enabled !== false, size: num(f.size, 3), position: f.position ?? 'outside', blendMode: bmFrom(f.blendMode, 'normal'), opacity: f.opacity ?? 1, hex: colorToHex(f.color, '#ff0000') }
    out.stroke = st; any = true
  }
  if (e.patternOverlay) skipped.push('パターンオーバーレイ')
  return { styles: any ? out : undefined, skipped }
}
